<template>
    <v-col cols="12">
        <v-form ref="modifiedCodeProductForm">

            <base-card>

                <v-card-title>
                    Código del producto en la representación gráfica
                </v-card-title>

                <v-card-subtitle class="mt-2">
                    Seleccione el campo que se mostrará como código del producto
                    en la sección de conceptos.
                </v-card-subtitle>

                <v-card-text>

                    <!-- ===================================================== -->
                    <!-- COMPAÑÍA + CONFIGURACIÓN -->
                    <!-- ===================================================== -->

                    <v-row align="center">

                        <!-- ================================================= -->
                        <!-- COMPAÑÍA -->
                        <!-- ================================================= -->

                        <v-col cols="12" sm="4">

                            <v-select ref="company" v-model="data.IdCompania" :items="detailClients" item-text="text"
                                item-value="IdCompania" label="Compañía" prepend-inner-icon="mdi-sitemap"
                                :disabled="!isAdmin" :error="companySelectorError" :rules="[validateCompanyField]"
                                :error-messages="helperSelectorText" @change="handleSelectCompany">

                                <!-- Buscador únicamente para administradores -->
                                <template v-if="isAdmin" v-slot:prepend-item>
                                    <v-list-item>

                                        <v-list-item-content>

                                            <v-list-item-title>

                                                <v-text-field class="search-field" v-model="search" placeholder="Buscar"
                                                    prepend-inner-icon="mdi-magnify" hide-details />

                                            </v-list-item-title>

                                        </v-list-item-content>

                                    </v-list-item>
                                </template>

                            </v-select>

                        </v-col>


                        <!-- ================================================= -->
                        <!-- CAMPO DEL CÓDIGO DEL PRODUCTO -->
                        <!-- ================================================= -->

                        <v-col v-if="companySelected" cols="12" sm="5">

                            <v-select ref="productCode" v-model="data.productCode" :items="productCodeOptions"
                                item-text="text" item-value="value" label="Campo del código del producto"
                                prepend-inner-icon="mdi-axis-arrow" :disabled="loadingConfig" :error="productCodeError"
                                :error-messages="productCodeHelperText" :rules="[validateProductCode]" />

                        </v-col>


                        <!-- ================================================= -->
                        <!-- BOTÓN GUARDAR -->
                        <!-- ================================================= -->

                        <v-col v-if="companySelected" cols="12" sm="3" class="d-flex align-center">

                            <v-btn class="ma-2" :disabled="loadingConfig"
                                style="background-color:rgb(204 33 40);color:white !important"
                                @click="saveConfiguration">
                                Guardar
                            </v-btn>

                        </v-col>

                    </v-row>


                    <!-- ===================================================== -->
                    <!-- ALERTAS -->
                    <!-- ===================================================== -->

                    <v-row>

                        <v-col cols="12">

                            <v-alert v-show="successAlert" type="success" text>
                                {{ msgSuccessAlert }}
                            </v-alert>

                            <v-alert v-show="errorAlert" type="warning" text>
                                {{ msgErrorAlert }}
                            </v-alert>

                        </v-col>

                    </v-row>

                </v-card-text>

            </base-card>

        </v-form>
    </v-col>
</template>


<script>

export default {

    name: 'ModifiedCodeProduct',

    data() {

        return {

            data: {
                IdCompania: null,
                productCode: null,
            },
            nameParam: 'MODIFIED_CODE_IN_RP',
            search: '',
            isAdmin: false,
            companySelected: false,
            loadingConfig: false,
            productCodeOptions: [
                {
                    text: 'ID Producto',
                    value: 0,
                },
                {
                    text: 'Referencia del producto',
                    value: 1,
                },
                {
                    text: 'Código de barras del producto',
                    value: 2,
                },
            ],
            companySelectorError: false,
            helperSelectorText: '',
            firstTimeCompany: true,
            productCodeError: false,
            productCodeHelperText: '',
            successAlert: false,
            msgSuccessAlert: '',
            errorAlert: false,
            msgErrorAlert: '',
        }
    },


    computed: {

        detailClients() {

            const companies = this.$store.state.clients.listClients.Mensaje || [];

            if (this.isAdmin) {

                return companies
                    .filter(
                        (compania) =>
                            compania.Activo === 1
                    )
                    .filter(
                        (compania) =>
                            compania.NombreCompania
                                .toLowerCase()
                                .includes(
                                    this.search.toLowerCase()
                                )
                    )
                    .map(
                        (compania) => ({
                            ...compania,
                            text: `[${compania.IdCompania}] ${compania.NombreCompania}`,
                        })
                    );
            }

            const sessionCompany = this.getSessionCompanyId();

            return companies
                .filter(
                    (compania) =>
                        compania.IdCompania === sessionCompany
                )
                .map(
                    (compania) => ({
                        ...compania,
                        text: `[${compania.IdCompania}] ${compania.NombreCompania}`,
                    })
                );
        },
    },


    created() {

        this.setUserRole();
        this.getListClients();

        if (!this.isAdmin) {
            this.setSessionCompany();
        }
    },


    methods: {

        // ============================================================
        // USUARIO / SESIÓN
        // ============================================================
        

        setUserRole() {

            const roleStorage = localStorage.getItem('role');

            if (!roleStorage) {
                this.isAdmin = false;
                return;
            }

            try {

                const role = JSON.parse(roleStorage);
                this.isAdmin = Array.isArray(role) &&
                    (
                        role.includes('Administrador') ||
                        role.includes('AdminI+D') ||
                        role.includes('Desarrollador')
                    );

            } catch (error) {
                console.error('Error interpretando role:', error);
                this.isAdmin = false;
            }
        },


        getSessionCompanyId() {

            const uid = localStorage.getItem('uid');
            if (!uid) {
                return null;
            }

            try {
                const sessionData = JSON.parse(atob(uid));
                return sessionData.idcompania;

            } catch (error) {
                console.error('Error interpretando uid:', error);
                return null;
            }
        },


        /**
         * Para un usuario que no es administrador,
         * establece automáticamente la compañía de la sesión.
         */
        setSessionCompany() {

            const companyId = this.getSessionCompanyId();

            if (!companyId) {
                return;
            }

            this.data.IdCompania = companyId;
            this.companySelected = true;

            this.getConfiguration();
        },


        // ============================================================
        // COMPAÑÍAS
        // ============================================================

        async getListClients() {
            await this.$store.dispatch(
                'clients/getListClients'
            );
        },


        /**
         * Se ejecuta cuando el administrador selecciona
         * una compañía.
         */
        handleSelectCompany(newValue) {

            this.clearAlerts();
            this.firstTimeCompany = false;
            this.data.productCode = null;
            this.companySelected = !!newValue;
            this.productCodeError = false;
            this.productCodeHelperText = '';

            if (!this.companySelected) {
                return;
            }

            this.getConfiguration();
        },


        /**
         * Validación de la compañía.
         */
        validateCompanyField(value) {

            if (!value && this.firstTimeCompany === false) {
                this.companySelectorError = true;
                this.helperSelectorText = 'El campo no puede estar vacío.';

                return false;
            }

            this.companySelectorError = false;
            this.helperSelectorText = '';

            return true;
        },


        // ============================================================
        // CONFIGURACIÓN
        // ============================================================

        async getConfiguration() {

            if (!this.data.IdCompania) {
                return;
            }

            this.loadingConfig = true;

            try {

                const response =
                    await this.$store.dispatch(
                        'companyParams/getConfigParam',
                        {
                            idCompany: this.data.IdCompania,
                            nameParam: this.nameParam,
                        }
                    );
                
                this.data.productCode = response === null || response === undefined || response === '' ? 0 : Number(response);

            } catch (error) {
                console.error('Error consultando configuración:', error);
                this.data.productCode = 0;

            } finally {
                this.loadingConfig = false;
            }
        },


        // ============================================================
        // VALIDACIÓN PRODUCT CODE
        // ============================================================

        validateProductCode(value) {

            if (value === null || value === undefined || value === '') {

                this.productCodeError = true;
                this.productCodeHelperText = 'Seleccione el campo que desea utilizar como código.';

                return false;
            }

            this.productCodeError = false;
            this.productCodeHelperText = '';

            return true;
        },


        // ============================================================
        // GUARDAR
        // ============================================================

        async saveConfiguration() {

            this.clearAlerts();

            if (!this.validateCompanyField(this.data.IdCompania)) {
                return;
            }

            if (!this.validateProductCode(this.data.productCode)) {
                return;
            }

            this.loadingConfig = true;

            try {
                const response =
                    await this.$store.dispatch(
                        'companyParams/updateConfiguration',
                        {
                            idCompany: this.data.IdCompania,
                            value: this.data.productCode,
                            nameParam: this.nameParam,
                            codeParam: 1,
                        }
                    );

                const message = this.getResponseMessage(response);
                this.showSuccess(message);

            } catch (error) {

                console.error('Error guardando configuración:', error);
                const message = this.getErrorMessage(error);
                this.showError(message);

            } finally {
                this.loadingConfig = false;
            }
        },


        // ============================================================
        // RESPUESTAS
        // ============================================================

        getResponseMessage(response) {

            if (!response) {
                return 'Configuración guardada correctamente.';
            }

            if (typeof response === 'string') {
                return response;
            }

            return (
                response.Mensaje ||
                response.message ||
                response.mensaje ||
                'Configuración guardada correctamente.'
            );
        },


        getErrorMessage(error) {

            if (error && error.response && error.response.data) {
                return (
                    error.response.data.Mensaje ||
                    error.response.data.message ||
                    error.response.data.mensaje ||
                    'No fue posible guardar la configuración.'
                );
            }

            if (error && error.message) {
                return error.message;
            }

            return 'No fue posible guardar la configuración.';
        },


        // ============================================================
        // ALERTAS
        // ============================================================

        showSuccess(message) {

            this.msgSuccessAlert = message || 'Configuración guardada correctamente.';
            this.successAlert = true;
            this.errorAlert = false;

            setTimeout(() => {
                this.successAlert = false;
            }, 3000);
        },


        showError(message) {

            this.msgErrorAlert = message || 'No fue posible guardar la configuración.';
            this.errorAlert = true;
            this.successAlert = false;

            setTimeout(() => {
                this.errorAlert = false;
            }, 3000);
        },


        clearAlerts() {

            this.successAlert = false;
            this.errorAlert = false;
            this.msgSuccessAlert = '';
            this.msgErrorAlert = '';
        },
    },
}

</script>